// src/components/InternalMessenger.jsx
import React, { useState } from 'react';

const InternalMessenger = ({ isOpen, onClose }) => {
  // State to switch between 'chat' and 'directory' view
  const [view, setView] = useState('chat'); // 'chat' or 'directory'
  const [searchQuery, setSearchQuery] = useState('');

  // Dummy Contacts Data (Replace with Firebase/DB later)
  const contacts = [
    { id: 1, name: 'The Architect', role: 'Admin', online: true, avatar: '🏛️' },
    { id: 2, name: 'Dr. Sarah (Tutor)', role: 'Mentor', online: true, avatar: '👩‍🏫' },
    { id: 3, name: 'Support Bot', role: 'AI', online: true, avatar: '🤖' },
    { id: 4, name: 'Admin Team', role: 'Support', online: false, avatar: '🛡️' },
  ];

  // Filter contacts based on search
  const filteredContacts = contacts.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 w-96 bg-gray-900 text-white p-6 rounded-xl border border-gray-700 shadow-2xl z-50 animate-slide-up font-sans">
      
      {/* --- HEADER --- */}
      <div className="flex justify-between items-center mb-4 border-b border-gray-700 pb-2">
        <div>
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
            THE SYNAPSE
          </h2>
          <span className="text-[10px] text-gray-500 uppercase tracking-widest">Secure Uplink v2.1</span>
        </div>
        <div className="flex gap-3">
            {/* View Switcher Icons */}
            <button 
                onClick={() => setView('directory')} 
                className={`text-gray-400 hover:text-white transition ${view === 'directory' ? 'text-cyan-400' : ''}`}
                title="Search Contacts"
            >
                🔍
            </button>
            <button 
                onClick={() => setView('chat')} 
                className={`text-gray-400 hover:text-white transition ${view === 'chat' ? 'text-cyan-400' : ''}`}
                title="Chat"
            >
                💬
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-red-400 ml-2">✕</button>
        </div>
      </div>

      {/* --- DIRECTORY VIEW (SEARCH) --- */}
      {view === 'directory' && (
        <div className="h-64 flex flex-col">
            <input 
                type="text" 
                placeholder="Search database..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-800 text-white text-sm p-2 rounded border border-gray-600 focus:outline-none focus:border-cyan-500 mb-3"
            />
            <div className="overflow-y-auto flex-1 space-y-2 pr-1 custom-scrollbar">
                {filteredContacts.map(contact => (
                    <div 
                        key={contact.id} 
                        className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded cursor-pointer transition border border-transparent hover:border-gray-700"
                        onClick={() => {
                            console.log(`Switched chat to ${contact.name}`);
                            setView('chat'); // In real app, this would set the active chat ID
                        }}
                    >
                        <div className="text-xl">{contact.avatar}</div>
                        <div className="flex-1">
                            <h4 className="text-sm font-bold text-gray-200">{contact.name}</h4>
                            <span className="text-[10px] text-gray-500 uppercase">{contact.role}</span>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${contact.online ? 'bg-green-500 shadow-[0_0_5px_#22c55e]' : 'bg-gray-600'}`}></div>
                    </div>
                ))}
                {filteredContacts.length === 0 && (
                    <div className="text-center text-gray-500 text-xs mt-4">No agents found.</div>
                )}
            </div>
        </div>
      )}

      {/* --- CHAT VIEW (MESSENGER) --- */}
      {view === 'chat' && (
        <>
            {/* Connection Controls */}
            <div className="grid grid-cols-4 gap-2 mb-4">
                <button className="flex flex-col items-center justify-center p-2 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-600 transition">
                    <span className="text-lg">📎</span>
                    <span className="text-[8px] mt-1 text-gray-300 uppercase">File</span>
                </button>
                <a href="https://discord.gg/BDatnMHH8j" target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center p-2 bg-gray-800 hover:bg-blue-900 rounded-lg border border-gray-600 hover:border-blue-500 transition no-underline">
                    <span className="text-lg">📹</span>
                    <span className="text-[8px] mt-1 text-blue-300 uppercase">Video</span>
                </a>
                <a href="https://discord.gg/BDatnMHH8j" target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center p-2 bg-gray-800 hover:bg-green-900 rounded-lg border border-gray-600 hover:border-green-500 transition no-underline">
                    <span className="text-lg">🖥️</span>
                    <span className="text-[8px] mt-1 text-green-300 uppercase">Share</span>
                </a>
                <a href="https://discord.gg/BDatnMHH8j" target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center p-2 bg-[#5865F2] hover:bg-[#4752c4] rounded-lg border border-transparent transition no-underline">
                    <span className="text-lg text-white">🏛️</span>
                    <span className="text-[8px] mt-1 text-white uppercase">Guild</span>
                </a>
            </div>

            {/* Chat Output */}
            <div className="bg-black rounded-lg p-3 h-32 border border-gray-800 text-sm text-gray-400 font-mono overflow-y-auto custom-scrollbar mb-2">
                <p className="opacity-50">&gt; Establishing secure connection...</p>
                <p className="opacity-50">&gt; Synapse active.</p>
                <p className="text-green-400 mt-2">&gt; Connected to: The Architect.</p>
            </div>

            {/* Input */}
            <div>
                <input 
                    type="text" 
                    placeholder="Type message..." 
                    className="w-full bg-gray-800 text-white text-sm p-2 rounded border border-gray-700 focus:outline-none focus:border-cyan-500" 
                />
            </div>
        </>
      )}

    </div>
  );
};

export default InternalMessenger;